import { useDashboard } from '../hooks/useSecurity';

interface DashStat {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}

interface ScanEntry {
  platform: string;
  scan_type: string;
  completed_at: string;
  severity_counts: { critical?: number; high?: number; medium?: number; low?: number };
  status: string;
}

interface TrendEntry {
  date: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

interface PlatformRisk {
  platform: string;
  risk_score: number;
}

function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-blue-100 text-blue-700',
    info: 'bg-gray-100 text-gray-600',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    running: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[severity] ?? 'bg-gray-100 text-gray-600'}`}>
      {severity}
    </span>
  );
}

function StatCard({ label, value, color, icon }: DashStat) {
  return (
    <div className="bg-white rounded-lg shadow p-6 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  );
}

function ScoreCircle({ score }: { score: number }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#16a34a' : score >= 60 ? '#d97706' : '#dc2626';

  return (
    <div className="relative w-36 h-36 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="10" />
        <circle
          cx="60" cy="60" r={radius} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-extrabold text-gray-900">{score}</span>
        <span className="text-xs text-gray-500">/ 100</span>
      </div>
    </div>
  );
}

function BarChart({ data }: { data: TrendEntry[] }) {
  if (!data || data.length === 0) return <div className="text-sm text-gray-400 py-8 text-center">No trend data available</div>;
  const maxVal = Math.max(...data.map(d => (d.critical || 0) + (d.high || 0) + (d.medium || 0) + (d.low || 0)), 1);
  const last14 = data.slice(-14);

  return (
    <div className="flex items-end gap-1 h-28 mt-2">
      {last14.map((d, i) => {
        const total = (d.critical || 0) + (d.high || 0) + (d.medium || 0) + (d.low || 0);
        const heightPct = (total / maxVal) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col justify-end gap-0" title={`${d.date}: ${total} vulns`}>
            <div style={{ height: `${heightPct}%` }} className="flex flex-col justify-end rounded-t overflow-hidden min-h-1">
              {d.critical > 0 && <div style={{ flex: d.critical }} className="bg-red-500" />}
              {d.high > 0 && <div style={{ flex: d.high }} className="bg-orange-400" />}
              {d.medium > 0 && <div style={{ flex: d.medium }} className="bg-yellow-400" />}
              {d.low > 0 && <div style={{ flex: d.low }} className="bg-blue-400" />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DashboardPage() {
  const { data, loading, error } = useDashboard();

  const dash = (data ?? {}) as Record<string, unknown>;

  const postureScore = (dash.posture_score as number) ?? 0;
  const totalVulns = (dash.total_vulnerabilities as number) ?? 0;
  const criticalVulns = (dash.critical_count as number) ?? 0;
  const totalDeps = (dash.total_dependencies as number) ?? 0;
  const scanCoverage = (dash.scan_coverage as number) ?? 0;
  const zeroDayAlert = dash.zero_day_alert as { cve_id: string; description: string } | null;
  const recentScans = ((dash.recent_scans as ScanEntry[]) ?? []).slice(0, 5);
  const trendData = (dash.vulnerability_trend as TrendEntry[]) ?? [];
  const platformRisks = (dash.platform_risks as PlatformRisk[]) ?? [];
  const maxRisk = Math.max(...platformRisks.map(p => p.risk_score), 1);

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded-lg" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="h-64 bg-gray-200 rounded-lg" />
            <div className="lg:col-span-2 h-64 bg-gray-200 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">Failed to load dashboard</p>
          <p className="text-red-500 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <span className="text-sm text-gray-400">Auto-refreshes every 30s</span>
      </div>

      {/* Zero-day alert banner */}
      {zeroDayAlert && (
        <div className="bg-red-600 text-white rounded-lg p-4 flex items-start gap-3">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="font-bold">Zero-Day Alert: {zeroDayAlert.cve_id}</p>
            <p className="text-sm text-red-100 mt-0.5">{zeroDayAlert.description}</p>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Vulnerabilities"
          value={totalVulns}
          color="bg-purple-100"
          icon={<svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
        />
        <StatCard
          label="Critical Vulnerabilities"
          value={criticalVulns}
          color="bg-red-100"
          icon={<svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
        />
        <StatCard
          label="Dependencies Tracked"
          value={totalDeps}
          color="bg-blue-100"
          icon={<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>}
        />
        <StatCard
          label="Scan Coverage %"
          value={scanCoverage}
          color="bg-green-100"
          icon={<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
      </div>

      {/* Posture score + trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Security Posture Score</h2>
          <ScoreCircle score={postureScore} />
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              {postureScore >= 80 ? 'Strong security posture' : postureScore >= 60 ? 'Moderate risk — action needed' : 'High risk — immediate action required'}
            </p>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">30-Day Vulnerability Trend</h2>
          <div className="flex gap-4 text-xs text-gray-500 mb-2">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-500 inline-block" />Critical</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-orange-400 inline-block" />High</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-yellow-400 inline-block" />Medium</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-400 inline-block" />Low</span>
          </div>
          <BarChart data={trendData} />
        </div>
      </div>

      {/* Platform risk scores + recent scans */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Platform Risk Scores</h2>
          {platformRisks.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No platform data yet.</p>
          ) : (
            <div className="space-y-3">
              {platformRisks.map(p => (
                <div key={p.platform}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 font-medium">{p.platform}</span>
                    <span className={`font-semibold ${p.risk_score >= 70 ? 'text-red-600' : p.risk_score >= 40 ? 'text-orange-500' : 'text-green-600'}`}>
                      {p.risk_score}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${p.risk_score >= 70 ? 'bg-red-500' : p.risk_score >= 40 ? 'bg-orange-400' : 'bg-green-500'}`}
                      style={{ width: `${(p.risk_score / maxRisk) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Recent Scans</h2>
          {recentScans.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No scans yet. Add platforms to get started.</p>
          ) : (
            <div className="space-y-3">
              {recentScans.map((scan, i) => (
                <div key={i} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{scan.platform}</p>
                    <p className="text-xs text-gray-500 capitalize">{scan.scan_type} · {new Date(scan.completed_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {scan.severity_counts?.critical != null && scan.severity_counts.critical > 0 && (
                      <span className="text-xs text-red-600 font-medium">{scan.severity_counts.critical}C</span>
                    )}
                    {scan.severity_counts?.high != null && scan.severity_counts.high > 0 && (
                      <span className="text-xs text-orange-500 font-medium">{scan.severity_counts.high}H</span>
                    )}
                    <SeverityBadge severity={scan.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
